import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { Download, Droplets, MoreHorizontal, Plus, Search } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { StatCardsSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { useMermas, useReverseMerma } from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import { usePagination } from "~/hooks/usePagination";
import {
  PeriodSelector,
  useCosteoPeriod,
} from "~/pages/costing/components/CosteoShared";
import { useProfileStore } from "~/store/profileStore";
import { UNIT_LABELS } from "~/types/purchasing";
import { exportCsv } from "~/utils/csv";
import { formatCurrency } from "~/utils/helpers";
import { litersToKg } from "~/utils/oilConversions";

import { MermaDialog } from "./MermaDialog";

import type { MermaRecord } from "~/types/production";

const num = (n: number) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n);

// Oils are stored in liters but counted in kg; display them in kg.
const isOilKg = (r: MermaRecord) =>
  r.item_kind === "oil" && r.density_kg_per_liter != null && r.density_kg_per_liter > 0;

const displayQty = (r: MermaRecord) =>
  isOilKg(r) ? litersToKg(r.quantity, r.density_kg_per_liter as number) : r.quantity;

const displayUnit = (r: MermaRecord) =>
  isOilKg(r) ? "kg" : (UNIT_LABELS[r.item_unit ?? ""] ?? r.item_unit ?? "");

export function MermasTab() {
  const { toast } = useToast();
  const role = useProfileStore((s) => s.role);
  const canWrite =
    role === "superadmin" || role === "admin" || role === "produccion-admin";

  const { periods, period, setPeriod } = useCosteoPeriod();
  const [plant, setPlant] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [revertTarget, setRevertTarget] = useState<MermaRecord | null>(null);

  const { data = [], isLoading, refetch } = useMermas({
    period: period || undefined,
    plant: plant === "all" ? undefined : plant,
  });
  const reverse = useReverseMerma();

  const openPeriods = useMemo(
    () => new Set(periods.filter((p) => p.status === "open").map((p) => p.period)),
    [periods]
  );
  const isRowOpen = (r: MermaRecord) =>
    openPeriods.has((r.movement_date ?? "").slice(0, 7));

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter(
      (r) =>
        (r.item_name ?? "").toLowerCase().includes(term) ||
        (r.item_code ?? "").toLowerCase().includes(term) ||
        (r.reason ?? "").toLowerCase().includes(term)
    );
  }, [data, search]);

  const kpis = useMemo(() => {
    let oils = 0;
    let mps = 0;
    let cost = 0;
    for (const r of rows) {
      if (r.item_kind === "oil") oils++;
      else mps++;
      cost += r.total_cost_clp || 0;
    }
    return { total: rows.length, oils, mps, cost };
  }, [rows]);

  const { page, setPage, pageCount, pageItems, total } = usePagination(rows, 20);

  const handleExport = () => {
    exportCsv(
      `mermas_${period || "todos"}.csv`,
      [
        "Fecha",
        "Tipo",
        "Código",
        "Ítem",
        "Planta",
        "Lote",
        "Cantidad",
        "Unidad",
        "Costo CLP",
        "Motivo",
        "Usuario",
      ],
      rows.map((r) => [
        dayjs(r.movement_date).format("DD/MM/YYYY"),
        r.item_kind === "oil" ? "Aceite" : "Materia Prima",
        r.item_code ?? "",
        r.item_name ?? "",
        r.plant ?? "",
        r.batch_number ?? "",
        num(displayQty(r)),
        displayUnit(r),
        Math.round(r.total_cost_clp || 0),
        r.reason ?? "",
        r.created_by_name ?? "",
      ])
    );
  };

  const handleRevert = () => {
    if (!revertTarget) return;
    reverse.mutate(revertTarget.id, {
      onSuccess: () => {
        toast({
          title: "Merma revertida",
          description: "Se restauró el stock del ingrediente.",
        });
        setRevertTarget(null);
        refetch();
      },
      onError: (error) => {
        setRevertTarget(null);
        toast({
          title: "No se pudo revertir",
          description: (error as Error).message,
          variant: "destructive",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <>
        <StatCardsSkeleton count={4} />
        <TableSkeleton rows={10} columns={9} />
      </>
    );
  }

  return (
    <>
      <p className="text-sm text-muted-foreground">
        El costo de la merma se identifica como pérdida y no afecta el costo del
        producto terminado.
      </p>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total mermas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{kpis.total}</p>
            <p className="text-xs text-muted-foreground">
              {kpis.mps} MP · {kpis.oils} aceites
            </p>
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Costo total merma
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-700">
              ${formatCurrency(kpis.cost)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{period || "—"}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <PeriodSelector periods={periods} value={period} onChange={setPeriod} />
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar ítem, código o motivo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-9"
          />
        </div>
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="h-9 w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las plantas</SelectItem>
            <SelectItem value="P1">Planta 1</SelectItem>
            <SelectItem value="P2">Planta 2</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={handleExport}
          disabled={rows.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Exportar CSV
        </Button>
        {canWrite && (
          <Button className="h-9 sm:ml-auto" onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Registrar merma
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Ítem</TableHead>
                  <TableHead>Planta</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="h-40 p-0">
                      <EmptyState
                        icon={Droplets}
                        title="Sin mermas"
                        description="No hay mermas para estos filtros."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {dayjs(r.movement_date).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {r.item_kind === "oil" ? "Aceite" : "Materia Prima"}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <div className="truncate font-medium">
                          {r.item_name ?? "—"}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {r.item_code}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.plant ?? "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.batch_number ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {num(displayQty(r))}{" "}
                        <span className="text-xs text-muted-foreground">
                          {displayUnit(r)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-red-700">
                        ${formatCurrency(r.total_cost_clp || 0)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-muted-foreground">
                        {r.reason ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.created_by_name ?? "—"}
                      </TableCell>
                      <TableCell>
                        {canWrite && isRowOpen(r) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-700"
                                onClick={() => setRevertTarget(r)}
                              >
                                Revertir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {rows.length > 0 && (
            <div className="border-t p-4">
              <TablePagination
                page={page}
                pageCount={pageCount}
                totalItems={total}
                pageSize={20}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <MermaDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => refetch()}
      />

      <AlertDialog
        open={!!revertTarget}
        onOpenChange={(o) => !o && setRevertTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revertir merma</AlertDialogTitle>
            <AlertDialogDescription>
              Se restaurará el stock de {revertTarget?.item_name} en{" "}
              {revertTarget?.plant} y la merma quedará anulada. ¿Confirmar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverse.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                handleRevert();
              }}
              disabled={reverse.isPending}
            >
              Revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
